import {
  Box,
  Typography,
  alpha,
  useTheme,
  useMediaQuery,
  Chip,
} from "@mui/material";
import {
  LayoutDashboard,
  Megaphone,
  Mail,
  Users,
  Tags,
  GitBranch,
  Zap,
  MessageCircle,
  Target,
  Building2,
  Kanban,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MarketingDashboard from "./components/Dashboard/MarketingDashboard";
import CampaignList from "./components/Campaigns/CampaignList";
import CampaignDetail from "./components/Campaigns/CampaignDetail";
import TemplateList from "./components/Templates/TemplateList";
import ContactList from "./components/Contacts/ContactList";
import SegmentList from "./components/Segments/SegmentList";
import SequenceList from "./components/Sequences/SequenceList";
import SequenceDetail from "./components/Sequences/SequenceDetail";
import AutomationList from "./components/Automations/AutomationList";
import WhatsAppLog from "./components/WhatsApp/WhatsAppLog";
import ScoringRules from "./components/Scoring/ScoringRules";
import MarketingOrgList from "./components/Organisations/MarketingOrgList";
import MarketingOrgDetail from "./components/Organisations/MarketingOrgDetail";
import PipelineTable from "./components/Organisations/PipelineTable";

const SECTION_KEYS = [
  { key: "dashboard", labelKey: "mkt_dashboard", icon: LayoutDashboard },
  { key: "campaigns", labelKey: "mkt_campaigns", icon: Megaphone },
  { key: "templates", labelKey: "mkt_templates", icon: Mail },
  { key: "contacts", labelKey: "mkt_contacts", icon: Users },
  { key: "organisations", labelKey: "mkt_organisations", icon: Building2 },
  { key: "pipeline", labelKey: "Pipeline", icon: Kanban },
  { key: "segments", labelKey: "mkt_segments", icon: Tags },
  { key: "sequences", labelKey: "mkt_sequences", icon: GitBranch },
  { key: "automations", labelKey: "mkt_automations", icon: Zap },
  { key: "whatsapp", labelKey: "mkt_whatsapp", icon: MessageCircle },
  { key: "scoring", labelKey: "mkt_scoring", icon: Target },
];

const MarketingScreen = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Derive active section from URL
  const pathParts = location.pathname.replace(/^\/admin\/marketing\/?/, "").split("/").filter(Boolean);
  const activeSection = SECTION_KEYS.find((s) => s.key === pathParts[0])?.key || "dashboard";
  const campaignId = activeSection === "campaigns" && pathParts[1] ? pathParts[1] : null;
  const sequenceId = activeSection === "sequences" && pathParts[1] ? pathParts[1] : null;
  const orgId = activeSection === "organisations" && pathParts[1] ? pathParts[1] : null;

  const handleSectionChange = (key) => {
    navigate(`/admin/marketing/${key}`);
  };

  const renderContent = () => {
    switch (activeSection) {
      case "dashboard":
        return <MarketingDashboard />;
      case "campaigns":
        if (campaignId) return <CampaignDetail campaignId={campaignId} />;
        return <CampaignList />;
      case "templates":
        return <TemplateList />;
      case "contacts":
        return <ContactList />;
      case "organisations":
        if (orgId === "new") return <MarketingOrgDetail />;
        if (orgId) return <MarketingOrgDetail orgId={orgId} />;
        return <MarketingOrgList />;
      case "segments":
        return <SegmentList />;
      case "sequences":
        if (sequenceId) return <SequenceDetail sequenceId={sequenceId} />;
        return <SequenceList />;
      case "automations":
        return <AutomationList />;
      case "whatsapp":
        return <WhatsAppLog />;
      case "pipeline":
        return <PipelineTable />;
      case "scoring":
        return <ScoringRules />;
      default:
        return <MarketingDashboard />;
    }
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 400,
            fontSize: "1.5rem",
            color: theme.palette.text.primary,
            mb: 0.5,
            fontFamily: "'Cormorant Garamond', Georgia, serif",
          }}
        >
          {t('admin_marketing')}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          {t('admin_marketing_description')}
        </Typography>
      </Box>

      {/* Sub-Navigation */}
      <Box
        sx={{
          display: "flex",
          gap: 0.75,
          mb: 3,
          flexWrap: "wrap",
          pb: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {SECTION_KEYS.map(({ key, labelKey, icon: Icon }) => {
          const isActive = activeSection === key;
          return (
            <Chip
              key={key}
              icon={<Icon size={14} strokeWidth={isActive ? 2 : 1.5} />}
              label={t(labelKey)}
              onClick={() => handleSectionChange(key)}
              variant={isActive ? "filled" : "outlined"}
              size="small"
              sx={{
                borderRadius: "8px",
                fontWeight: isActive ? 600 : 500,
                fontSize: "0.8rem",
                height: 32,
                transition: "all 0.2s",
                bgcolor: isActive
                  ? alpha(theme.palette.primary.main, 0.12)
                  : "transparent",
                color: isActive
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
                borderColor: isActive
                  ? alpha(theme.palette.primary.main, 0.3)
                  : theme.palette.divider,
                "& .MuiChip-icon": {
                  color: isActive
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                },
                "&:hover": {
                  bgcolor: isActive
                    ? alpha(theme.palette.primary.main, 0.15)
                    : alpha(theme.palette.divider, 0.5),
                  color: isActive
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
                },
              }}
            />
          );
        })}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>{renderContent()}</Box>
    </Box>
  );
};

export default MarketingScreen;
